/**
 * Automatic Problem Report Utility
 * 
 * Creates problem reports automatically when evaluation uploads or processing fail.
 * Uses the same 'problem-reports' collection as manual user reports.
 * Includes rate limiting to avoid flooding Firestore.
 */
import { addDoc, collection } from 'firebase/firestore';
import { db } from '../firebaseInit';
import { getConsoleHistory } from '../globalSetup';

// Rate limiting: max 1 auto-report per error type per 60 seconds
const recentReports = new Map();
const RATE_LIMIT_MS = 60_000;

/**
 * Create an automatic problem report for evaluation failures.
 * 
 * @param {Object} options
 * @param {string} options.errorType - Category of error (e.g. 'evaluation_upload_failed', 'evaluation_processing_failed', 're_evaluation_failed')
 * @param {string} options.errorMessage - The error message
 * @param {string} [options.userEmail] - Email of the user who hit the error
 * @param {string} [options.userRole] - Role of the user
 * @param {string} [options.teamId] - Team ID
 * @param {Object} [options.context] - Additional context (evaluationId, callType, etc.)
 */
export async function createAutoReport({
  errorType,
  errorMessage,
  userEmail = 'unknown',
  userRole = 'unknown',
  teamId = 'unknown',
  context = {},
}) {
  try {
    // Rate limit: skip if we recently filed the same error type
    const rateKey = `${errorType}:${userEmail}`;
    const lastReport = recentReports.get(rateKey);
    if (lastReport && Date.now() - lastReport < RATE_LIMIT_MS) {
      console.log(`🔇 Auto-report rate-limited for ${errorType} (${userEmail})`);
      return null;
    }
    recentReports.set(rateKey, Date.now());

    // Gather console logs for debugging context
    let consoleLogs = [];
    try {
      consoleLogs = typeof getConsoleHistory === 'function'
        ? getConsoleHistory().slice(-30)
        : [];
    } catch {
      // getConsoleHistory may not be available in all contexts
    }

    const reportData = {
      // Required fields for Firestore security rules
      description: `[Auto] ${errorType}: ${errorMessage}`.substring(0, 2000),
      timestamp: new Date(),
      currentPage: 'CallEvaluator',

      // User context
      userEmail,
      userRole,
      userTeam: teamId,

      // Technical info
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent?.substring(0, 200) : 'server',
      url: typeof window !== 'undefined' ? window.location?.href : '',
      appVersion: process.env.REACT_APP_VERSION || 'unknown',

      // Auto-report metadata
      isAutoReport: true,
      errorType,
      errorContext: {
        ...context,
        errorMessage: String(errorMessage).substring(0, 1000),
      },

      // Debug info
      consoleLogs,
      debugInfo: {
        environment: process.env.NODE_ENV,
        autoGenerated: true,
        reportedAt: new Date().toISOString(),
      },
    };

    const docRef = await addDoc(collection(db, 'problem-reports'), reportData);
    console.log(`📝 Auto problem report created: ${docRef.id} (${errorType})`);
    return docRef.id;
  } catch (reportError) {
    // Never let the reporting itself crash the app
    console.error('❌ Failed to create auto problem report:', reportError);
    return null;
  }
}
